import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, Tooltip } from "@fluentui/react-components";

import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  Text,
  createTableColumn,
} from "@fluentui/react-components";
import { PersonSupportFilled } from "@fluentui/react-icons";

// Components
import UserAvatar from "./UserAvatar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useSelectedUsersContext } from "../context/SelectedUsersContext";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { readUsers } from "../../users/services/userApi";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "auto",
    width: "100%",
  },
  scrollArea: {
    flex: 1,
    minWidth: 0,
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

const UsersDataGrid = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { value: selectedUsers, setValue: SetSelectedUsers } = useSelectedUsersContext();

  // Errors
  const [errors, setErrors] = useState([]);

  const { data = [], isFetching, error } = useItemQuery(["users"], readUsers, true);

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, t]);

  if (isFetching) return <Spinner label={t("generic.loading")} />;

  const columns = [
    createTableColumn({
      columnId: "name",
      renderHeaderCell: () => t("generic.name"),
      renderCell: (item) => (
        <TableCellLayout>
          <TableCellLayout>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <UserAvatar user={item} />
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Text weight="semibold">
                  {item.fname} {item.lname}
                </Text>
                {/* Optional secondary text */}
                {/* <Text size={200}>Frontend Architect</Text> */}
              </div>
              {item.is_agent > 0 && (
                <Tooltip content="Agent" positioning="after" withArrow>
                  <PersonSupportFilled />
                </Tooltip>
              )}
            </div>
          </TableCellLayout>
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "email",
      renderHeaderCell: () => t("generic.email"),
      renderCell: (item) => <TableCellLayout>{item.email}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "positition_title",
      renderHeaderCell: () => t("generic.positionTitle"),
      renderCell: (item) => <TableCellLayout>{item.title}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "phone",
      renderHeaderCell: () => t("generic.phone"),
      renderCell: (item) => <TableCellLayout>{item.phone}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "mobile",
      renderHeaderCell: () => t("generic.mobile"),
      renderCell: (item) => <TableCellLayout>{item.mobile}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "site",
      renderHeaderCell: () => t("generic.site"),
      renderCell: (item) => <TableCellLayout>{item.site}</TableCellLayout>,
    }),
  ];

  const baseWidth = 100;
  const columnSizingOptions = {
    name: {
      idealWidth: baseWidth * 2,
      minWidth: baseWidth * 2,
    },
    email: {
      idealWidth: baseWidth * 2,
      minWidth: baseWidth * 2,
    },
  };

  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const newSelection = data.filter((l) => ids.includes(l.id));
    SetSelectedUsers(newSelection);
  };

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <DataGrid
        items={data}
        columns={columns}
        columnSizingOptions={columnSizingOptions}
        selectedItems={selectedUsers.map((item) => item.id)}
        getRowId={(item) => item.id}
        focusMode="composite"
        resizableColumns
        selectionMode="single"
        onSelectionChange={handleSelectionChange}
      >
        <DataGridHeader>
          <DataGridRow
            className={styles.stickyHeaderRow}
            selectionCell={{
              checkboxIndicator: { "aria-label": "Select all rows" },
            }}
          >
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>

        <div className={styles.scrollArea}>
          <DataGridBody>
            {({ item, rowId }) => (
              <DataGridRow
                key={rowId}
                selectionCell={{
                  checkboxIndicator: { "aria-label": "Select row" },
                }}
              >
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </div>
      </DataGrid>
    </div>
  );
};

export default UsersDataGrid;
